import { create } from "zustand";

type Question = {
  ID: number;
  QuestionRU: string;
  QuestionKZ: string;
  QuestionEN: string;
  Answer: string;
};

type Questionnaire = {
  TestsID: number;
  questionares: Question[];
};

type UserModel = {
  ID: number;
  Name: string;
  Email: string;
  Phone: string;
};

export type Options = {
  DeclarerTypeID?: number;
  DeclarerRoleID?: number;
  organizationModel: { BranchID?: number };
};

type State = {
  data: {
    questionare: Questionnaire;
    userModel: UserModel;
  } & Options;
};

type Actions = {
  setAnswer: (id: number, answer: string) => void;
  setPersonalData: (data: UserModel) => void;
  setOptions: (options: Options) => void;
  setAnswers: (answers: { id: number; value: string }[]) => void;
};

type Store = State & Actions;

const useDataStore = create<Store>((set, get) => ({
  data: {
    DeclarerTypeID: undefined,
    DeclarerRoleID: undefined,
    organizationModel: { BranchID: undefined },
    userModel: { ID: 0, Name: "", Email: "", Phone: "" },
    questionare: {
      TestsID: 1,
      questionares: [
        {
          "ID": 1,
          "QuestionRU": "Пожалуйста, опишите ситуацию, о которой вы хотите сообщить, с указанием точного времени, даты и места происшествия. Уточните, кто именно был вовлечен в данную ситуацию и на каком основании вы считаете, что эти лица причастны к происходящему.",
          "QuestionKZ": "Оқиғаның нақты уақытын, күнін және орнын көрсете отырып, хабарлағыңыз келетін жағдайды сипаттаңыз. Бұл жағдайға нақты кім қатысқанын және қандай негізде бұл адамдар не болып жатқанына қатысы бар деп ойлайтыныңызды нақтылаңыз.",
          "QuestionEN": "Please describe the situation you wish to report, indicating the exact time, date and location of the incident. Specify who exactly was involved in this situation and on what basis you believe that these persons were involved in what is happening.",
          "Answer": ""
        },
        {
          "ID": 2,
          "QuestionRU": "Сохраняется ли данная ситуация на текущий момент, и сколько конкретных случаев данного нарушения вам известно?",
          "QuestionKZ": "Бұл жағдай қазіргі уақытта сақталуда ма және сіз осы бұзушылықтың нақты қанша жағдайын білесіз?",
          "QuestionEN": " Is this situation still ongoing, and how many specific cases of this violation are you aware of?",
          "Answer": ""
        },
        {
          "ID": 3,
          "QuestionRU": "Кто еще может быть осведомлен о данной ситуации, включая лиц, не являющихся сотрудниками компании? Если такие лица есть, уточните, кто они и где работают.",
          "QuestionKZ": "Компания қызметкерлері болып табылмайтын адамдарды қоса алғанда, бұл жағдай туралы тағы кім хабардар болуы мүмкін? Егер мұндай адамдар болса, олардың кім екенін және қайда жұмыс істейтінін анықтаңыз.",
          "QuestionEN": "Who else may be aware of this situation, including persons who are not employees of the company? If so, specify who they are and where they work.",
          "Answer": ""
        },
        {
          "ID": 4,
          "QuestionRU": "Располагаете ли вы какими-либо документальными подтверждениями предоставленной информации? Готовы ли вы предоставить эти доказательства?",
          "QuestionKZ": "Сізде берілген ақпараттың қандай да бір құжаттық растамасы бар ма? Сіз бұл дәлелдерді беруге дайынсыз ба?",
          "QuestionEN": "Do you have any documentary evidence to support the information provided? Are you prepared to provide such evidence?",
          "Answer": ""
        },
        {
          "ID": 5,
          "QuestionRU": "Сообщали ли вы о данной ситуации кому-либо из представителей АО «Фонд национального благосостояния «Самрук-Казына»? Если да, то кому именно и когда?",
          "QuestionKZ": "Сіз бұл жағдай туралы 'Самұрық-Қазына' ұлттық әл-ауқат қоры' АҚ өкілдерінің біріне хабарладыңыз ба? Олай болса, кімге және қашан?",
          "QuestionEN": "Have you reported this situation to any representatives of JSC National Welfare Fund Samruk-Kazyna? If so, to whom exactly and when?",
          "Answer": ""
        },
        {
          "ID": 6,
          "QuestionRU": "Есть ли у вас дополнительная информация, которой вы хотели бы поделиться?",
          "QuestionKZ": "Сізде бөліскіңіз келетін қосымша ақпарат бар ма?",
          "QuestionEN": "Do you have any additional information you would like to share?",
          "Answer": ""
        },
      ],
    },
  },
  setPersonalData: (payload) => {
    set((state) => ({ data: { ...state.data, userModel: { ...payload } } }));
  },
  setAnswer: (id, answer) => {
    const state = get();
    set(() => ({
      ...state,
      questionare: {
        ...state.data.questionare,
        questionares: state.data.questionare.questionares.map((q) =>
          q.ID === id ? { ...q, Answer: answer } : q,
        ),
      },
    }));
  },
  setOptions: (options: Options) => {
    set((state) => ({
      data: {
        ...state.data,
        DeclarerTypeID: options.DeclarerTypeID,
        DeclarerRoleID: options.DeclarerRoleID,
        organizationModel: { BranchID: options.organizationModel.BranchID },
      },
    }));
  },
  setAnswers: (answers) => {
    set((state) => ({
      data: {
        ...state.data,
        questionare: {
          ...state.data.questionare,
          questionares: state.data.questionare.questionares.map((q) => {
            const answer = answers.find((a) => a.id === q.ID - 1);
            return answer ? { ...q, Answer: answer.value } : q;
          }),
        },
      },
    }));
  },
}));

export default useDataStore;
